import React from "react";
import { useTranslation } from "react-i18next";
import { FiMinusCircle } from "react-icons/fi";
import { GoPlusCircle } from "react-icons/go";
import { useDispatch, useSelector } from "react-redux";
import { setNumberOfGuest } from "../../redux/slice/apartmentRental/apartmentRentalSlice";
import { RootState } from "../../redux/store";
import { numberOfGuestsSelected } from "../../redux/slice/bookingCreation/bookingCreationSlice";

interface NumericInputProps {
  min?: number;
  max?: number;
  step?: number;
  onChange?: (value: number) => void;
  /**
   * i18n key of the field's label, used as its accessible name — the
   * category's head-count word (#3035, M2). Defaults to today's "number of
   * guests".
   */
  labelKey?: string;
}

const NumericInput: React.FC<NumericInputProps> = ({
  min = 1,
  max = 100,
  step = 1,
  onChange,
  labelKey = "apartment.number_guests",
}) => {
  const { numberOfGuest } = useSelector((state: RootState) => state.apartment);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const commit = (value: number) => {
    const clamped = Math.min(Math.max(value, min), max);
    dispatch(setNumberOfGuest(clamped));
    dispatch(numberOfGuestsSelected({ numberOfGuests: clamped }));
    onChange?.(clamped);
  };

  const handleDecrease = () => commit(numberOfGuest - step);
  const handleIncrease = () => commit(numberOfGuest + step);

  const isMinReached = numberOfGuest <= min;
  const isMaxReached = numberOfGuest >= max;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === "") return; // allow transient empty during edit
    const newValue = parseInt(raw, 10);
    if (!Number.isNaN(newValue)) commit(newValue);
  };

  const guestsLabel = t(labelKey);

  return (
    <div className="flex items-center space-x-2">
      <button
        type="button"
        onClick={handleDecrease}
        disabled={isMinReached}
        aria-label={t("apartment.decrement_guests", {
          defaultValue: "Decrease guests",
        })}
        className={`flex items-center justify-center min-w-[44px] min-h-[44px] rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-opacity ${
          isMinReached
            ? "opacity-50 cursor-not-allowed"
            : "opacity-100 hover:opacity-80"
        }`}
      >
        <FiMinusCircle className="text-foreground/80 w-6 h-6" aria-hidden="true" />
      </button>
      <input
        type="number"
        inputMode="numeric"
        pattern="[0-9]*"
        min={min}
        max={max}
        step={step}
        aria-label={guestsLabel}
        value={numberOfGuest}
        onChange={handleInputChange}
        className="w-24 text-center text-black h-14 border rounded-xl border-primary
         focus:outline-none focus:border-primary
         [appearance:textfield]
         [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0
         [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0"
      />
      <button
        type="button"
        onClick={handleIncrease}
        disabled={isMaxReached}
        aria-label={t("apartment.increment_guests", {
          defaultValue: "Increase guests",
        })}
        className={`flex items-center justify-center min-w-[44px] min-h-[44px] rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-opacity ${
          isMaxReached
            ? "opacity-50 cursor-not-allowed"
            : "opacity-100 hover:opacity-80"
        }`}
      >
        <GoPlusCircle className="text-foreground/80 w-6 h-6" aria-hidden="true" />
      </button>
    </div>
  );
};

export default NumericInput;
